import React, { useRef, useState } from "react";
import { SavedSearchModal } from "./modal/SavedSearchModal";
import { EditNameInput } from "./edit/EditNameInput";
import { SavedSearchSettingsChannels } from "./channels/SavedSearchSettingsChannels";
import { CreateButton, CreateSearchHeader } from "./styles";
import { closableErrorToast, closableSuccessToast } from "../utils";
import {
  Category,
  Channel,
  BaseSavedSearch,
  SavedSearchInput,
  NotificationChannels,
} from "../model/model";
import { useSavedSearchService } from "../services/useSavedSearchService";
import { SavedSearchValidator } from "../validators/SavedSearchValidator";
import { useAppTranslation } from "../locales/translate";

export interface Props {
  isOpen: boolean;
  closeModal: () => void;
  search: SavedSearchInput;
  onCreate?: (savedSearch: BaseSavedSearch) => void;
  onError?: (error: unknown) => void;
}

const initialEnabledChannels: NotificationChannels = {
  [Channel.WEB]: true,
  [Channel.EMAIL]: true,
  [Channel.PUSH]: true,
};

export const CreateSavedSearchModal = ({
  isOpen,
  closeModal,
  search,
  onCreate,
  onError,
}: Props) => {
  const savedSearchService = useSavedSearchService(Category.MARKET);
  const focusRef = useRef(null);
  const [name, setName] = useState(search.description ?? "");
  const [isNameValid, setIsNameValid] = useState(true);
  const [enabledChannels, setEnabledChannels] = useState<NotificationChannels>(
    initialEnabledChannels
  );
  const [isCreateDisabled, setIsCreateDisabled] = useState(false);

  const { t } = useAppTranslation();

  const isChannelSelected = (channel: Channel) =>
    Boolean(enabledChannels[channel]);

  const toggleChannel = (channel: Channel) => {
    setEnabledChannels((prev) => ({ ...prev, [channel]: !prev[channel] }));
  };

  const createSavedSearch = () => {
    setIsCreateDisabled(true);

    savedSearchService
      .createSavedSearch(name, enabledChannels, search)
      .then((createdSavedSearch) => {
        closeModal();
        closableSuccessToast(t("TOAST_SEARCH_CREATE_SUCCESS"));
        if (onCreate) onCreate(createdSavedSearch);
      })
      .catch((error) => {
        closableErrorToast(t("TOAST_SEARCH_CREATE_ERROR"));
        if (onError) onError(error);
      })
      .finally(() => setIsCreateDisabled(false));
  };

  const handleNameChange = (changedName: string) => {
    if (SavedSearchValidator.isNameValid(changedName)) {
      setIsNameValid(true);
    }
    setName(changedName);
  };

  const handleCreateSearch = () => {
    if (SavedSearchValidator.isNameValid(name)) {
      createSavedSearch();
    } else {
      setIsNameValid(false);
    }
  };

  return (
    <SavedSearchModal
      isOpen={isOpen}
      onDismiss={closeModal}
      focusRef={focusRef}
      body={
        <>
          <CreateSearchHeader>
            <h1>{t("CREATE_SEARCH_MODAL_TITLE")}</h1>
            <EditNameInput
              label={t("CREATE_MODAL_NAME_INPUT_LABEL")}
              name={name}
              editName={handleNameChange}
              isInvalid={!isNameValid}
              onEnter={handleCreateSearch}
            />
          </CreateSearchHeader>
          <SavedSearchSettingsChannels
            toggleChannel={toggleChannel}
            isChannelSelected={isChannelSelected}
            category={Category.MARKET}
          />
        </>
      }
      footer={
        <CreateButton
          primary
          role="button"
          onClick={handleCreateSearch}
          disabled={isCreateDisabled}
        >
          {t("CREATE_MODAL_CONFIRM_BUTTON_TEXT")}
        </CreateButton>
      }
    />
  );
};
